import { Box, Card, FormHelperText, Stack, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { SVButton, SVForm } from './core';
import LogoImage from './LogoImage';
import { OTPFormFields } from '@/types/common';

type OtpFormProps = {
  formMethods: UseFormReturn<OTPFormFields, any, OTPFormFields>;
  isLoading: boolean;
  isLoadingResend: boolean;
  content: string;
  handleResendOtp: () => void;
  handleSubmit: (data: OTPFormFields) => Promise<void>;
  hasFailedOnce: boolean;
  submitForm?: (onSubmit: (data: OTPFormFields) => Promise<void>) => void;
};

const otpInputStyles = {
  '&.MuiOtpInput-Box': {
    gap: '10px',
  },
  '& .MuiOtpInput-TextField': {
    minWidth: '36.5px',
    height: '48px',
    mt: 2,
    mb: 4,
    '@media (min-width: 600px)': {
      width: '68px',
      height: '48px',
      gap: 2,
      mx: 3,
      mt: 2,
      mb: 6,
    },
  },
};

export default function OtpForm({
  formMethods,
  isLoading,
  isLoadingResend,
  content,
  handleResendOtp,
  handleSubmit,
  hasFailedOnce = false,
  submitForm,
}: OtpFormProps) {
  return (
    <>
      <LogoImage />
      <Card>
        <Stack spacing={2}>
          <Typography variant="h6" component="h1">
            Enter Your One Time Password
          </Typography>
          <Typography variant="body1">{content}</Typography>
          <SVForm<OTPFormFields> methods={formMethods} onSubmit={handleSubmit}>
            <Controller
              name="code"
              control={formMethods.control}
              rules={{
                required: true,
                validate: value =>
                  value.length === 6 || 'Please enter all 6 digits',
              }}
              render={({ field, fieldState }) => (
                <Box>
                  <MuiOtpInput
                    sx={otpInputStyles}
                    {...field}
                    length={6}
                    validateChar={character => {
                      return /^[0-9]$/.test(character);
                    }}
                    onChange={value => {
                      field.onChange(value);
                      // Only auto-submit if there hasn't been a failed attempt and there are 6 digits
                      if (value.length === 6 && !hasFailedOnce && submitForm) {
                        submitForm(handleSubmit);
                      }
                    }}
                  />
                  {fieldState.error && (
                    <FormHelperText error sx={{ mb: 2 }}>
                      {fieldState.error.message ||
                        'Verification code is required'}
                    </FormHelperText>
                  )}
                  {hasFailedOnce && !isLoading && field.value.length === 6 && (
                    <FormHelperText sx={{ my: 1 }}>
                      Please click the Done button to submit
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
            <SVButton
              sx={{ width: '100%' }}
              isLoading={isLoading}
              type="submit">
              Done
            </SVButton>
          </SVForm>
          <SVButton
            variant="outlined"
            onClick={handleResendOtp}
            isLoading={isLoadingResend}
            type="button">
            Resend Code
          </SVButton>
        </Stack>
      </Card>
    </>
  );
}
